/**
 * Sprint T-21 · Fase 1b · Push Web (arquitectura unificada)
 *
 * Módulo helper para el sistema de Web Push T-21 (backend `IntentBroker +
 * PushWebProvider + PushSubscription`). SUSTITUYE al legacy Sprint G (`lib/push.js`)
 * a medida que los consumidores se migren; ambos coexisten hasta cierre T-21.
 *
 * Endpoints backend consumidos (bajo `/api/notifications`):
 *   · GET    /push-subscriptions/vapid-public-key   (público)
 *   · POST   /push-subscriptions/subscribe          (auth dual: user | customer)
 *   · GET    /push-subscriptions/me                 (auth dual)
 *   · DELETE /push-subscriptions/{sub_id}           (auth dual)
 *   · GET    /channels/health                       (auth)
 *
 * Uso:
 *   import { subscribeDevice, listMyDevices, unsubscribeDevice } from '../lib/pushT21';
 *   import { customerApi } from '../lib/customerAuth';
 *   import { api } from '../lib/api';
 *
 *   // En Portal del Cliente:
 *   const subs = await listMyDevices(customerApi);
 *   await subscribeDevice(customerApi);
 *
 *   // En Portal del Taller (Settings.jsx):
 *   const subs = await listMyDevices(api);
 *
 * Filosofía T-21 · "Un solo dispatcher, un solo Studio, muchas plumas": el
 * componente que consume esta lib no sabe si el usuario es customer o user;
 * el backend lo resuelve con `get_push_subscriber`.
 */
import { BACKEND_URL } from "./api";

const SW_URL = "/sw.js";

// ------------------------------------------------------------
// Detección de soporte
// ------------------------------------------------------------

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPermissionState() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

// ------------------------------------------------------------
// Utilidades
// ------------------------------------------------------------

/** Convierte una VAPID public key (base64url) a Uint8Array (crypto.subtle input). */
export function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== "string") {
    throw new Error("VAPID key vacía o inválida");
  }
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Extrae etiqueta legible desde User-Agent (browser + OS). Puro · testeable. */
export function parseUserAgent(ua) {
  ua = (ua || "").toString();
  const uaLower = ua.toLowerCase();
  let os = "Desconocido";
  if (uaLower.includes("iphone") || uaLower.includes("ipad")) os = "iOS";
  else if (uaLower.includes("android")) os = "Android";
  else if (uaLower.includes("mac os") || uaLower.includes("macintosh")) os = "macOS";
  else if (uaLower.includes("windows")) os = "Windows";
  else if (uaLower.includes("linux")) os = "Linux";

  let browser = "Navegador";
  if (uaLower.includes("edg/")) browser = "Edge";
  else if (uaLower.includes("opr/") || uaLower.includes("opera")) browser = "Opera";
  else if (uaLower.includes("chrome/") && !uaLower.includes("edg/")) browser = "Chrome";
  else if (uaLower.includes("firefox")) browser = "Firefox";
  else if (uaLower.includes("safari")) browser = "Safari";

  return { os, browser, label: `${browser} · ${os}` };
}

/** Formatea fecha ISO a "hace X". Puro · testeable. */
export function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - then) / 1000));
    if (diffSec < 60) return "hace unos segundos";
    if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
    if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
    if (diffSec < 30 * 86400) return `hace ${Math.floor(diffSec / 86400)} d`;
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

// ------------------------------------------------------------
// Service Worker
// ------------------------------------------------------------

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

// ------------------------------------------------------------
// API T-21
// ------------------------------------------------------------

/** VAPID public-key es endpoint público · usamos fetch directo sin auth. */
export async function fetchVapidKey() {
  const res = await fetch(`${BACKEND_URL}/api/notifications/push-subscriptions/vapid-public-key`);
  if (!res.ok) throw new Error("No se pudo obtener la VAPID public key");
  const json = await res.json();
  if (!json.configured || !json.public_key) {
    throw new Error("El servidor no tiene VAPID configurado");
  }
  return json.public_key;
}

/**
 * Suscribe el dispositivo actual al Push Web T-21.
 * @param {axios} apiClient  Instancia axios ya autenticada (api | customerApi).
 * @param {Object} [opts]
 * @param {string} [opts.deviceLabel]  Etiqueta opcional para el dispositivo.
 * @returns {Promise<{id, endpoint_hash, device_label, created_at}>}
 */
export async function subscribeDevice(apiClient, opts = {}) {
  if (!isPushSupported()) throw new Error("Tu navegador no soporta notificaciones push.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permiso de notificaciones denegado.");

  const reg = await registerServiceWorker();
  if (!reg) throw new Error("No se pudo registrar el Service Worker.");
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const publicKey = await fetchVapidKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = sub.toJSON();
  const keys = json.keys || {};
  const deviceLabel = (opts.deviceLabel && opts.deviceLabel.trim())
    || parseUserAgent(navigator.userAgent).label;

  const payload = {
    endpoint: json.endpoint,
    p256dh: keys.p256dh,
    auth: keys.auth,
    kind: "web",
    device_label: deviceLabel.slice(0, 120),
  };
  const { data } = await apiClient.post("/notifications/push-subscriptions/subscribe", payload);
  return data;
}

/**
 * Lista las suscripciones activas del usuario actual.
 * @param {axios} apiClient
 * @returns {Promise<Array<{id, kind, endpoint_hash, device_label, user_agent, created_at, last_used_at}>>}
 */
export async function listMyDevices(apiClient) {
  const { data } = await apiClient.get("/notifications/push-subscriptions/me");
  return Array.isArray(data) ? data : [];
}

/**
 * Elimina UNA suscripción por id. Si es el dispositivo actual, también limpia
 * localmente la subscription del navegador.
 * @param {axios} apiClient
 * @param {string} subId
 */
export async function unsubscribeDevice(apiClient, subId) {
  await apiClient.delete(`/notifications/push-subscriptions/${subId}`);
}

/**
 * Desuscribe el dispositivo actual completamente: cancela la subscription local
 * del navegador y elimina TODAS las suscripciones del usuario cuyos endpoint
 * coincidan (por endpoint_hash) con el actual.
 * @param {axios} apiClient
 */
export async function disableCurrentDevice(apiClient) {
  if (!isPushSupported()) return;
  // 1) cancelar en navegador
  let currentHash = null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      currentHash = "…" + sub.endpoint.slice(-8);
      await sub.unsubscribe();
    }
  } catch (e) { /* noop */ }

  // 2) borrar en backend la sub correspondiente
  if (currentHash) {
    try {
      const subs = await listMyDevices(apiClient);
      const match = subs.find((s) => s.endpoint_hash === currentHash);
      if (match) await unsubscribeDevice(apiClient, match.id);
    } catch (e) { /* noop */ }
  }
}

/**
 * Estado agregado listo para renderizar en la UI.
 * @param {axios} apiClient
 */
export async function getPushStatus(apiClient) {
  const supported = isPushSupported();
  const permission = getPermissionState();
  let currentSubscribed = false;
  let devices = [];
  if (supported) {
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_URL);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      currentSubscribed = !!sub;
    } catch (e) { /* noop */ }
    if (apiClient) {
      try { devices = await listMyDevices(apiClient); } catch (e) { /* noop */ }
    }
  }
  return { supported, permission, currentSubscribed, devices };
}

// ------------------------------------------------------------
// Sprint T-24 · Silent Resubscribe
// ------------------------------------------------------------
//
// Sube el dispositivo actual al Push T-21 SIN pedir permisos nuevos.
// Reglas de seguridad (DECISIÓN Founder · fase Shadow-ready T-24 · endurecidas
// tras audit adicional 2026-02-13):
//   1. NUNCA llama a Notification.requestPermission() · sólo lee el estado.
//   2. Si permission !== "granted" → aborta con reason="permission_not_granted".
//   3. Si no hay Push soportado → aborta con reason="push_unsupported".
//   4. Detecta subscription actual del navegador · si su applicationServerKey
//      coincide con el VAPID T-21 → reason="already_t21_registered" +
//      re-registra en backend por si no estaba (idempotente).
//   5. Si existe sub con VAPID distinto (Legacy · applicationServerKey ≠ T-21)
//      → NO hace unsubscribe() para NO ROMPER LA SUB LEGACY del navegador.
//      Aborta con reason="legacy_subscription_present_no_swap".
//      La spec Web Push permite SÓLO UNA sub por origen; forzar el swap
//      destruiría el canal Legacy sin autorización. La coexistencia real
//      Legacy+T-21 en el mismo browser NO es posible por spec, así que en
//      esta fase priorizamos la seguridad de Legacy sobre la adopción T-21.
//   6. Si no hay sub y permission === granted → intenta subscribe silencioso
//      con VAPID T-21 (users nuevos o que resetaron el navegador).
//   7. Registra la nueva sub en backend `POST /push-subscriptions/subscribe`
//      (idempotente por endpoint_hash).
//
// Devuelve `{ ok, reason, subscription?, backend_response? }` para que el
// caller decida si mostrar invitación explícita al usuario.
//
// NUNCA elimina Legacy: `users.push_token` y VAPID Legacy quedan intactos;
// la sub Legacy del browser TAMBIÉN queda intacta (regla #5 endurecida).

// --- Telemetría PII-free (Fase T-24 · aditiva) ---------------------------
// Mapea el `reason` interno a un outcome agregable (success | blocked |
// error | skipped). Cualquier motivo no listado aquí se clasifica como
// "error" para no perder señales.
const _OUTCOME_MAP = {
  // success
  already_t21_registered: "success",
  resubscribed_new: "success",
  subscribe_success: "success",
  // blocked (spec/perms/legacy)
  push_unsupported: "skipped",
  permission_not_granted: "skipped",
  permission_denied: "skipped",
  service_worker_unavailable: "blocked",
  service_worker_error: "blocked",
  legacy_subscription_present_no_swap: "blocked",
  silent_subscribe_blocked_by_browser: "blocked",
  subscribe_blocked_by_browser: "blocked",
  // error
  vapid_fetch_failed: "error",
  backend_registration_failed: "error",
  subscribe_failed: "error",
  unknown_error: "error",
};

function _browserFamily(ua) {
  const s = (ua || "").toLowerCase();
  if (s.includes("edg/")) return "edge";
  if (s.includes("opr/") || s.includes("opera")) return "opera";
  if (s.includes("samsungbrowser")) return "samsung";
  if (s.includes("firefox")) return "firefox";
  if (s.includes("chrome/") && !s.includes("edg/")) return "chrome";
  if (s.includes("safari")) return "safari";
  return "other";
}

function _platform(ua) {
  const s = (ua || "").toLowerCase();
  if (s.includes("iphone") || s.includes("ipad") || s.includes("ipod")) return "ios";
  if (s.includes("android")) return "android";
  if (s.includes("mac os") || s.includes("macintosh")) return "macos";
  if (s.includes("windows")) return "windows";
  if (s.includes("cros")) return "chromeos";
  if (s.includes("linux")) return "linux";
  return "other";
}

/**
 * POST fire-and-forget de un evento agregado al backend T-24.
 * Nunca bloquea el flujo del hook · silent swallow en caso de error.
 */
async function _emitTelemetry(apiClient, extras) {
  try {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const outcome = _OUTCOME_MAP[extras.reason] || "error";
    const payload = {
      outcome,
      reason: extras.reason || "unknown_error",
      browser_family: _browserFamily(ua),
      platform: _platform(ua),
      permission_state:
        typeof Notification !== "undefined" ? Notification.permission : "unsupported",
      push_supported: isPushSupported(),
      service_worker_available:
        typeof navigator !== "undefined" && "serviceWorker" in navigator,
      push_manager_available: typeof window !== "undefined" && "PushManager" in window,
      had_existing_subscription: !!extras.had_existing_subscription,
      application_server_key_match:
        typeof extras.application_server_key_match === "boolean"
          ? extras.application_server_key_match
          : null,
      subscribe_result: extras.subscribe_result || null,
    };
    // Fire-and-forget · timeout corto para no bloquear.
    await apiClient.post(
      "/notifications/migration/silent-resubscribe-telemetry",
      payload,
      { timeout: 5000 },
    );
  } catch (e) {
    // Silencio absoluto · esto es diagnóstico, no debe romper flujo.
  }
}

export async function silentResubscribeToT21(apiClient) {
  const result = { ok: false, reason: null, subscription: null, backend_response: null };
  // Métricas auxiliares para telemetría (rellenadas conforme avanza el flujo).
  const meta = {
    had_existing_subscription: false,
    application_server_key_match: null,
    subscribe_result: null,
  };
  const finalize = async () => {
    try {
      await _emitTelemetry(apiClient, {
        reason: result.reason || "unknown_error",
        had_existing_subscription: meta.had_existing_subscription,
        application_server_key_match: meta.application_server_key_match,
        subscribe_result: meta.subscribe_result,
      });
    } catch (_e) { /* noop */ }
  };

  if (!isPushSupported()) {
    result.reason = "push_unsupported";
    await finalize();
    return result;
  }
  if (getPermissionState() !== "granted") {
    result.reason = "permission_not_granted";
    await finalize();
    return result;
  }

  let reg;
  try {
    reg = await registerServiceWorker();
    if (!reg) {
      result.reason = "service_worker_unavailable";
      await finalize();
      return result;
    }
    await navigator.serviceWorker.ready;
  } catch (e) {
    result.reason = "service_worker_error";
    await finalize();
    return result;
  }

  // Comparación cripto-segura: buscamos VAPID T-21 pública y la comparamos
  // con la applicationServerKey de la sub actual (bytes crudos).
  let t21Vapid;
  try {
    t21Vapid = await fetchVapidKey();
  } catch (e) {
    result.reason = "vapid_fetch_failed";
    await finalize();
    return result;
  }
  const t21KeyBytes = urlBase64ToUint8Array(t21Vapid);

  let existingSub = null;
  try {
    existingSub = await reg.pushManager.getSubscription();
  } catch (e) { /* noop */ }
  meta.had_existing_subscription = !!existingSub;

  // Helper local para comparar Uint8Array bytes-a-bytes.
  const _bytesEqual = (a, b) => {
    if (!a || !b || a.byteLength !== b.byteLength) return false;
    const va = new Uint8Array(a);
    const vb = new Uint8Array(b);
    for (let i = 0; i < va.length; i++) if (va[i] !== vb[i]) return false;
    return true;
  };

  if (existingSub) {
    const currentKey = existingSub.options?.applicationServerKey;
    const keyMatch = !!(currentKey && _bytesEqual(currentKey, t21KeyBytes));
    meta.application_server_key_match = keyMatch;
    if (keyMatch) {
      // Sub actual YA es T-21 · registrarla en backend si no lo está (idempotente).
      const json = existingSub.toJSON();
      const keys = json.keys || {};
      try {
        const { data } = await apiClient.post(
          "/notifications/push-subscriptions/subscribe",
          {
            endpoint: json.endpoint,
            p256dh: keys.p256dh,
            auth: keys.auth,
            kind: "web",
            device_label: parseUserAgent(navigator.userAgent).label.slice(0, 120),
          },
        );
        result.ok = true;
        result.reason = "already_t21_registered";
        result.subscription = existingSub;
        result.backend_response = data;
      } catch (e) {
        result.reason = "backend_registration_failed";
      }
      await finalize();
      return result;
    }

    // Sub existente con VAPID distinto (Legacy) → NO tocamos.
    // Regla Founder #5 endurecida: NO se ejecuta unsubscribe() para no romper
    // la sub Legacy del navegador. La adopción T-21 en este navegador ocurrirá
    // cuando el user pase por un flujo explícito (PushInvitationBanner
    // migrado a T-21 · o wipe manual del navegador).
    result.reason = "legacy_subscription_present_no_swap";
    await finalize();
    return result;
  }

  // Aquí NO hay sub activa · intentamos subscribe silencioso con VAPID T-21.
  let newSub;
  try {
    newSub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: t21KeyBytes,
    });
    meta.subscribe_result = "subscribe_success";
  } catch (e) {
    // Puede fallar en Safari/iOS que a veces requieren user gesture.
    result.reason = "silent_subscribe_blocked_by_browser";
    meta.subscribe_result = "subscribe_blocked_by_browser";
    await finalize();
    return result;
  }

  const json = newSub.toJSON();
  const keys = json.keys || {};
  try {
    const { data } = await apiClient.post(
      "/notifications/push-subscriptions/subscribe",
      {
        endpoint: json.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
        kind: "web",
        device_label: parseUserAgent(navigator.userAgent).label.slice(0, 120),
      },
    );
    result.ok = true;
    result.reason = "resubscribed_new";
    result.subscription = newSub;
    result.backend_response = data;
  } catch (e) {
    result.reason = "backend_registration_failed";
  }
  await finalize();
  return result;
}
